package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.*
import com.tixforgigs.common.core.enums.ClientType
import com.tixforgigs.common.core.enums.View
import com.tixforgigs.common.core.extensions.isNotNull
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.models.embed.PostMessageEmbedAuthDTO
import com.tixforgigs.common.core.util.IdHelper.isEmbedded
import com.tixforgigs.common.core.util.LocalizationUtil.currentTranslationLocale
import com.tixforgigs.common.core.util.StorageUtil.deleteStorageItem
import com.tixforgigs.common.core.util.StorageUtil.getStorageItem
import com.tixforgigs.common.core.util.StorageUtil.setStorageItem
import com.tixforgigs.common.http.ApiPostPath
import com.tixforgigs.common.http.HttpClient.AUTH_CLIENT_ID
import com.tixforgigs.common.http.HttpClient.AUTH_URL
import com.tixforgigs.common.http.HttpClient.BASE_URL
import com.tixforgigs.common.http.HttpClient.apiV4Post
import com.tixforgigs.common.http.HttpClient.oauthTokenRequest
import de.comahe.i18n4k.language
import io.ktor.client.plugins.auth.providers.*
import io.ktor.http.*
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.format.DateTimeComponents
import kotlinx.serialization.json.Json
import org.w3c.dom.MessageEvent
import org.w3c.dom.Window
import org.w3c.dom.url.URL
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant
import kotlin.uuid.Uuid


internal expect fun getPlatformUniqueId(): Uuid

internal expect suspend fun getDeviceId(): String

object AuthUtil {

  /////////////////////////////////////////////////// MARK: PROPERTIES
  val currentBearerToken: MutableStateFlow<AuthToken?> by lazy {
    MutableStateFlow(
      value = getStorageItem<AuthToken>(StorageUtil.StorageKey.AuthToken)
    )
  }

  val isUserLoggedIn: MutableStateFlow<Boolean> by lazy {
    MutableStateFlow(
      value = getStorageItem<AuthToken>(StorageUtil.StorageKey.AuthToken).isExpiredAccessToken.not()
    )
  }

  val uniqueId: Uuid by lazy { getPlatformUniqueId() }

  init {
    window.addEventListener("message", { event ->
      runCatching { event.unsafeCast<MessageEvent>() }.getOrNull()?.let { messageEvent ->
        runCatching {
          Json.decodeFromString<PostMessageEmbedAuthDTO>(messageEvent.data.toString())
        }.getOrNull()?.let { authMessage ->
          authMessage.authToken?.let { authToken ->
            setStorageItem<AuthToken>(
              key = StorageUtil.StorageKey.AuthToken,
              item = authToken,
              serializer = AuthToken.serializer()
            )
            redirectInternal(
              onCompletionWithPath = { redirectWithPath(it) },
              onCompletionFallback = {
                redirectWithPath(View.RELATIVE_BASE_PATH_ROOT.path)
              }
            )
          }
        }
      }
    })
  }

  //////////////////////////////////////////////////// MARK: FUNCTIONS
  suspend fun deviceId() = getDeviceId()

  suspend fun authExpiredChecker() {
    while (getStorageItem<AuthToken>(StorageUtil.StorageKey.AuthToken).isNotNull) {
      val authToken = getStorageItem<AuthToken>(StorageUtil.StorageKey.AuthToken)
      if (authToken.isExpiredRefreshToken) {
        deleteStorageItem(StorageUtil.StorageKey.AuthToken)
      }
      delay(1.seconds)
    }
  }

  suspend fun authTokenRequests(
    code: String,
    grantType: GrantType,
    clientType: ClientType,
    onCompletionWithPath: (path: String) -> Unit,
    onCompletionFallback: () -> Unit,
  ) {
    oauthTokenRequest(code, grantType, clientType)?.let { authToken ->
      window.opener?.let { openerWindow ->
        openerWindow.unsafeCast<Window>().postMessage(
          message = Json.encodeToString(
            value = PostMessageEmbedAuthDTO(
              authToken = authToken
            )
          ),
          targetOrigin = "*" //TODO allow only own origins
        )
        window.close()
      }
      setStorageItem<AuthToken>(
        key = StorageUtil.StorageKey.AuthToken,
        item = authToken,
        serializer = AuthToken.serializer()
      )
      redirectInternal(onCompletionWithPath, onCompletionFallback)
    }
  }

  fun authAuthorizeRequest(
    scopes: List<String>,
    onCompletionWithUrl: (String) -> Unit,
  ) {
    buildUrl {
      takeFrom(AUTH_URL)
      appendPathSegments("oauth", "authorize")
      parameters.append("response_type", "code")
      parameters.append("client_id", AUTH_CLIENT_ID)
      parameters.append("state", uniqueId.toString())
      parameters.append("scope", scopes.joinToString(" "))
      parameters.append(
        "redirect_uri",
        "${BASE_URL}${View.TOKEN.path}"
      )
    }.also { url ->
      onCompletionWithUrl(url.toString())
    }
  }

  suspend fun login(
    email: String,
    password: String
  ) {
    apiV4Post<TokenRedirectUrlResponse>(
      type = ApiPostPath.AccountLogin(
        body = TokenAuthRequest(
          clientId = AUTH_CLIENT_ID,
          userName = email,
          password = password,
          returnUrl = "${BASE_URL}${View.AUTHORIZE.path}",
          tokenMode = TokenMode.REDIRECT_URL
        )
      )
    )?.let { response ->
      if (isEmbedded) {
        window.open(response.redirectUrl, "_blank")
      } else {
        setStorageItem(
          key = StorageUtil.StorageKey.AuthGrantType,
          item = GrantType.AUTHORIZATION_CODE,
          serializer = GrantType.serializer()
        )
        window.location.href = response.redirectUrl
      }
    }
  }

  private fun redirectInternal(
    onCompletionWithPath: (path: String) -> Unit,
    onCompletionFallback: () -> Unit,
  ) {
    getStorageItem<String>(StorageUtil.StorageKey.LoginStartLocation)?.let { path ->
      deleteStorageItem(StorageUtil.StorageKey.LoginStartLocation)
      onCompletionWithPath(path)
    } ?: run {
      onCompletionFallback()
    }
  }

  suspend fun signup(
    email: String,
    password: String,
    familyName: String,
    givenName: String,
    dateOfBirth: Instant?,
  ) {
    apiV4Post<Unit>(
      type = ApiPostPath.AccountUserCreate(
        RegisterRequest(
          emailAddress = email,
          password = password,
          familyName = familyName,
          givenName = givenName,
          locale = currentTranslationLocale.value.language,
          dateOfBirth = dateOfBirth
        )
      )
    )?.let {
      login(email, password)
    }
  }

  fun redirectWithPath(path: String) {
    val url = URL(window.location.href)
    url.pathname = path
    url.search = String()
    url.hash = String()
    window.location.assign(url.toString())
  }

  fun AuthToken?.toBearerTokensOrNull() = this?.let { BearerTokens(it.accessToken, it.refreshToken) }

  val AuthToken.accessTokenExpiresInstant: Instant?
    get() = tryCatchSentry {
      DateTimeComponents.Formats.RFC_1123
        .parse(expires)
        .toInstantUsingOffset()
    }.getOrNull()

  val AuthToken.refreshTokenExpiresInstant: Instant?
    get() = tryCatchSentry {
      DateTimeComponents.Formats.RFC_1123
        .parse(tfgRefreshExpires)
        .toInstantUsingOffset()
    }.getOrNull()

  val AuthToken.createdInstant: Instant?
    get() = tryCatchSentry {
      DateTimeComponents.Formats.RFC_1123
        .parse(issued)
        .toInstantUsingOffset()
    }.getOrNull()

  val AuthToken?.isExpiredAccessToken: Boolean
    get() = this?.accessTokenExpiresInstant?.let {
      it < Clock.System.now()
    }.orElse { true }

  val AuthToken?.isExpiredRefreshToken: Boolean
    get() = this?.refreshTokenExpiresInstant?.let {
      it < Clock.System.now()
    }.orElse { true }
}
